//cloudinary
export const CLOUDINARY_VIDEO_URL_PREFIX =
  'https://edume-res.cloudinary.com/video/upload/';
export const CLOUDFRONT_VIDEO_URL_PREFIX =
  'https://d13dt4xa7zzqg.cloudfront.net/video/upload/';

export const stripCloudinaryUrlPrefix = (url: string) =>
  url.split(CLOUDINARY_VIDEO_URL_PREFIX)[1];

export const stripUploadUrlPrefix = (url: string) =>
  new URL(url).pathname.split('/upload/')[1];

export const getVideoUrl = (name: string) => {
  let processedName = name;

  if (processedName.includes('c_crop') && processedName.includes('c_limit')) {
    /**
     * If 'c_crop' is present, ensure 'c_limit,h_...,w_.../' is after the crop params as Cloudinary applies params sequentially.
     */
    processedName = processedName.replace(
      /(c_limit,h_\d+,w_\d+)\/(.*?)(c_crop\/)/,
      '$2$3$1/'
    );
  }

  return 'https://d13dt4xa7zzqg.cloudfront.net/video/upload/' + processedName;
};

/**
 * Takes a Cloudinary video URL and adds trim and crop parameters
 * based on the provided parameters.
 *
 * Will only append these params if they are needed (e.g. the video has been cropped or trimmed),
 * so as not to trigger unnecessary Cloudinary transformations.
 */
export const buildVideoUrlWithCloudinaryCropParams = ({
  videoUrl,
  x,
  y,
  width,
  height,
  videoStart,
  videoEnd,
  duration,
  zoom,
  minZoom,
}: {
  videoUrl: string;
  duration: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  videoStart?: number;
  videoEnd?: number;
  zoom?: number;
  minZoom?: number;
}) => {
  const hasCrop = zoom !== minZoom;
  const hasTimeParams = videoStart !== 0 || videoEnd !== duration;

  const params = [
    hasCrop && `x_${x}`,
    hasCrop && `y_${y}`,
    hasCrop && `w_${width}`,
    hasCrop && `h_${height}`,
    hasTimeParams && `so_${videoStart}`,
    hasTimeParams && `eo_${videoEnd}`,
    hasCrop && 'c_crop',
  ].filter(Boolean);

  return params.length > 0 ? `${params.join(',')}/${videoUrl}` : videoUrl;
};

export const getCloudinaryVideoUrl = (name: string) =>
  CLOUDINARY_VIDEO_URL_PREFIX + name;

export const GUIDES_VIDEO_URL_PREFIX = 'https://d1ej1il77mtywa.cloudfront.net';
export const getGuideVideoUrl =
  (prefix: string, env: string) => (video: string) => {
    const base = prefix || GUIDES_VIDEO_URL_PREFIX;
    const videoExtensionReplacedMp4 = video.replace(/\.[^.]+$/, '.mp4');
    return `${base}/optimized-videos/${env}/${videoExtensionReplacedMp4}`;
  };

export const getVideoThumb = (name: string) => {
  const youTubeVideoThumbFromUrl = getYouTubeVideoThumbFromUrl(name);
  return youTubeVideoThumbFromUrl
    ? youTubeVideoThumbFromUrl
    : getVideoUrl(name).replace(/\.((mp4)|(mov))/, '.png');
};

/*Supported YouTube URL formats:
      Standard Videos:
      https://www.youtube.com/watch?v=dQw4w9WgXcQ
      https://youtu.be/dQw4w9WgXcQ
      Shorts:
      https://www.youtube.com/shorts/dQw4w9WgXcQ
      Embed:
      https://www.youtube.com/embed/dQw4w9WgXcQ
      Old Paths:
      https://www.youtube.com/user/someUser/u/1/dQw4w9WgXcQ
      Google APIs:
      https://youtube.googleapis.com/v/dQw4w9WgXcQ
  */
export const getYouTubeIdFromUrl = (url: string) => {
  const idRegex =
    /^(?:https?:\/\/)?(?:(?:www\.)?(?:youtube(?:-nocookie)?|youtube.googleapis)\.com.*(?:v\/|v=|vi=|vi\/|e\/|embed\/|user\/.*\/u\/\d+\/|shorts\/)|youtu\.be\/)([_0-9a-zA-Z-]+)/g;
  const matches = idRegex.exec(url);
  return matches?.[1];
};

export const getYouTubeVideoThumbFromUrl = (url: string) => {
  const youTubeVideoId = getYouTubeIdFromUrl(url);
  return youTubeVideoId
    ? `https://img.youtube.com/vi/${youTubeVideoId}/hqdefault.jpg` //note maxresdefault.jpg would remove bars but it's not always available
    : false;
};

export const getThumbnailByUrl = ({
  source,
  url,
}: {
  source: 'youtube' | 'cloudinary';
  url: string;
}): string => {
  switch (source) {
    case 'youtube':
      return `https://img.youtube.com/vi/${getYouTubeIdFromUrl(
        url
      )}/hqdefault.jpg`;
    case 'cloudinary':
      //cloudinary supports changing video extension to jpg to get default thumbnail
      return `https://edume-res.cloudinary.com/video/upload/${url}`.replace(
        // eslint-disable-next-line no-useless-escape
        /\.[^\.]*$/,
        '.jpg'
      );
    default:
      return '';
  }
};

export const getSecondsToSMPTE = (time: number, frameRate: number): string => {
  const dt = new Date();
  const format = 'hh:mm:ss:ff';
  dt.setHours(0);
  dt.setMinutes(0);
  dt.setSeconds(0);
  dt.setMilliseconds(time * 1000);
  function wrap(n: number): string {
    return n < 10 ? '0' + n : String(n);
  }
  function replacer(f: string): string {
    switch (f) {
      case 'hh':
        return wrap(dt.getHours() < 13 ? dt.getHours() : dt.getHours() - 12);
      case 'mm':
        return wrap(dt.getMinutes());
      case 'ss':
        return wrap(dt.getSeconds());
      case 'ff':
        return wrap(Math.floor((time % 1) * frameRate));
      default:
        return '';
    }
  }
  return format.replace(/hh|mm|ss|ff/g, replacer);
};

export const formatVideoDuration = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const formattedMins = mins < 10 ? `0${mins}` : mins;
  const formattedSecs = secs < 10 ? `0${secs}` : secs;

  if (hrs > 0) {
    const formattedHrs = hrs < 10 ? `0${hrs}` : hrs;
    return `${formattedHrs}:${formattedMins}:${formattedSecs}`;
  } else {
    return `${formattedMins}:${formattedSecs}`;
  }
};
